import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { FONTS } from '../../constants/config';
import { useRoute, useNavigation, useFocusEffect, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsive } from '../../hooks/useResponsive';
import { PageContainer, Navbar, Breadcrumb, ActivityCalendar } from '../../components';
import DetailHeader from './components/DetailHeader';
import DetailStatsRow from './components/DetailStatsRow';
import StatisticsCards from './components/StatisticsCards';
import KPIsSection from './components/KPIsSection';
import ProgramsSection from './components/ProgramsSection';
import OutputsSection from './components/OutputsSection';
import DevelopmentPlan from './components/DevelopmentPlan';
import DetailTeachersTable from './components/DetailTeachersTable';
import StudentsTable from './components/StudentsTable';
import TalentsSkeleton from './components/TalentsSkeleton';
import { talentSpecializationsApi, talentActivitiesApi, talentProjectsApi } from '../../services/apiService';
import { API_BASE_HOST } from '../../constants/config';
import { SpecializationDetailData } from './types';
import { CalendarEvent } from '../../components/ActivityCalendar/types';
import { MainStackParamList } from '../../navigation/MainNavigator';

type ScreenRouteProp = RouteProp<MainStackParamList, 'SpecializationDetail'>;

export default function SpecializationDetailScreen() {
  const route = useRoute<ScreenRouteProp>();
  const navigation = useNavigation<NativeStackNavigationProp<MainStackParamList>>();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { user } = useAuth();
  const userType = user?.profile?.user_type;
  const isCoordinator = userType === 'coordinator';
  const readOnly = userType === 'strategy_team';
  const isStudent = userType === 'student';
  const noCreate = readOnly || isCoordinator || isStudent;
  // Coordinators are assigned to a talent by the entity employee or the
  // strategy team (and superuser) — both may add/import/remove them here.
  const canManageCoordinators =
    userType === 'org_employee' || userType === 'strategy_team' || userType === 'superuser';
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 24;

  const specializationIdParam = route.params?.id;
  const specializationName = route.params?.name ? decodeURIComponent(route.params.name) : '';
  const entityName = route.params?.entityName;
  const routeDescription = route.params?.description;

  const [isLoading, setIsLoading] = useState(true);
  const [specializationId, setSpecializationId] = useState<number | undefined>(specializationIdParam);
  const [data, setData] = useState<SpecializationDetailData>({
    id: 0, title: '', description: '', weight: 0, studentsCount: 0, teachersCount: 0, activitiesCount: 0, kpisCount: 0,
    programsCount: 0, overallProgress: 0, statusDistribution: { excellent: 0, good: 0, needsImprovement: 0 },
    statistics: [], kpis: [], programs: [], outputs: [], developmentPlan: { title: '', content: '' },
    planAttachments: [],
  });
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [planFileName, setPlanFileName] = useState('');
  const [budget, setBudget] = useState<number | null>(null);
  const [firstProjectId, setFirstProjectId] = useState<number | undefined>();
  const [departmentName, setDepartmentName] = useState(entityName || '');

  useFocusEffect(useCallback(() => {
    const fetchData = async () => {
      setIsLoading(true);

      // If we don't have an id (deep link by name), resolve it first
      let resolvedId = specializationId;
      if (!resolvedId && specializationName) {
        try {
          const list = await talentSpecializationsApi.list();
          const specs = list?.results || list;
          if (Array.isArray(specs)) {
            const match = specs.find((s: any) => s.name === specializationName);
            if (match) {
              resolvedId = match.id;
              setSpecializationId(match.id);
            }
          }
        } catch (e) {
          console.log('[SpecializationDetail] Failed to resolve name to id:', e);
        }
      }
      if (!resolvedId) {
        setIsLoading(false);
        return;
      }

      // Fetch specialization details
      try {
        const resp = await talentSpecializationsApi.details(resolvedId);
        if (resp) {
          if (resp.department_name) {
            setDepartmentName(resp.department_name);
          }
          const transformed: SpecializationDetailData = {
            id: resp.id,
            title: resp.name || '',
            description: resp.description || '',
            weight: resp.weight ?? 0,
            studentsCount: resp.students_count ?? 0,
            teachersCount: resp.teachers_count ?? 0,
            activitiesCount: resp.activities_count ?? 0,
            kpisCount: resp.kpis?.length ?? 0,
            programsCount: resp.projects_count ?? 0,
            overallProgress: resp.overall_progress ?? 0,
            statusDistribution: resp.status_distribution ?? { excellent: 0, good: 0, needsImprovement: 0 },
            statistics: resp.statistics ?? [],
            kpis: (resp.kpis || []).map((k: any) => ({
              id: k.kpi || k.id,
              linkId: k.id,
              name: k.kpi_name || k.name || '',
              targetValue: k.target_value ?? 0,
              achievedValue: k.achieved_value ?? 0,
              unit: k.unit || 'count',
              isAchieved: (k.achieved_value ?? 0) >= (k.target_value ?? 0),
            })),
            programs: [], // Will be overridden below if projects API succeeds
            outputs: (resp.outputs || []).map((o: any) => ({
              id: o.id,
              title: o.title || '',
              image: (() => { const src = o.thumbnail || o.file; return src ? (src.startsWith('http') ? src : `${API_BASE_HOST}${src}`) : undefined; })(),
              date: o.created_at || undefined,
            })),
            developmentPlan: resp.development_plan ?? { title: '', content: '' },
            planAttachments: resp.plan_attachments ?? [],
          };
          setData(transformed);
          setBudget(resp.budget != null ? Number(resp.budget) : null);
          if (resp.plan_file) setPlanFileName(resp.plan_file.split('/').pop() || '');
        }
      } catch (e) {
        console.log('[SpecializationDetail] API failed:', e);
      }
      // Fetch projects for this specialization
      try {
        const projectsResp = await talentProjectsApi.list({ specialization: resolvedId });
        const projectsList = projectsResp?.results || projectsResp;
        if (Array.isArray(projectsList) && projectsList.length > 0) {
          const sorted = [...projectsList].sort((a: any, b: any) =>
            (a.created_at || '').localeCompare(b.created_at || '')
          );
          setData(prev => ({
            ...prev,
            programs: sorted.map((p: any) => ({
              id: p.id,
              name: p.name || '',
              progress: p.progress_percentage ?? 0,
            })),
          }));
          if (sorted.length > 0) setFirstProjectId(sorted[0].id);
        }
      } catch (e) {
        console.log('[SpecializationDetail] Projects API failed:', e);
      }
      // Fetch calendar events
      try {
        const events = await talentActivitiesApi.calendar({ specialization: resolvedId });
        if (Array.isArray(events) && events.length > 0) {
          setCalendarEvents(events);
        }
      } catch (e) {
        console.log('[SpecializationDetail] Calendar API failed:', e);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [specializationId, specializationName]));

  // Re-fetch projects when screen comes back into focus (e.g. after adding a project)
  useFocusEffect(
    useCallback(() => {
      const refetchProjects = async () => {
        const id = specializationId;
        if (!id) return;
        try {
          const projectsResp = await talentProjectsApi.list({ specialization: id });
          const projectsList = projectsResp?.results || projectsResp;
          if (Array.isArray(projectsList)) {
            // Sort by created_at ascending so oldest appears first (rightmost in RTL)
            const sorted = [...projectsList].sort((a: any, b: any) =>
              (a.created_at || '').localeCompare(b.created_at || '')
            );
            setData(prev => ({
              ...prev,
              programs: sorted.map((p: any) => ({
                id: p.id,
                name: p.name || '',
                progress: p.progress_percentage ?? 0,
              })),
              programsCount: sorted.length,
            }));
            if (sorted.length > 0) setFirstProjectId(sorted[0].id);
          }
        } catch (e) {
          // silent — initial fetch already handled this
        }
      };
      refetchProjects();
    }, [specializationId])
  );

  const handleNavigateToTalents = () => {
    navigation.navigate('Talents');
  };

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.container, { padding: containerPadding }]}>
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: t('navigation.talents'), route: 'Talents' },
              { label: t('specializationDetail.title') },
            ]}
          />

          {isLoading ? (
            <TalentsSkeleton />
          ) : (
            <View style={styles.sections}>
              <DetailHeader
                title={data.title || specializationName}
                weight={data.weight}
                description={data.description || routeDescription}
                entityName={departmentName}
                onEditSchedule={!readOnly && !isCoordinator && !isStudent && specializationId ? () => navigation.navigate('EditTalents', { mode: 'schedule', specId: specializationId }) : undefined}
              />

              <DetailStatsRow
                studentsCount={data.studentsCount}
                teachersCount={data.teachersCount}
                activitiesCount={data.activitiesCount}
                kpisCount={data.kpisCount}
                programsCount={data.programsCount}
                overallProgress={data.overallProgress}
                statusDistribution={data.statusDistribution}
              />

              {/* Budget — set by the entity; shown to everyone when present */}
              {budget != null && !isStudent && (
                <View style={styles.budgetCard}>
                  <Text style={styles.budgetValue}>
                    {budget.toLocaleString('en-US')} {isRTL ? 'د.إ' : 'AED'}
                  </Text>
                  <Text style={styles.budgetLabel}>{isRTL ? 'الميزانية' : 'Budget'}</Text>
                </View>
              )}

              <StatisticsCards statistics={data.statistics} />

              {/* KPIs — hidden for students */}
              {!isStudent && (
                <KPIsSection kpis={data.kpis} readOnly={noCreate} onAddKPI={() => navigation.navigate('AddTalentKPI' as any, { specializationId })} onViewKPI={(kpiLinkId) => navigation.navigate('TalentKPIDetail' as any, { kpiLinkId })} />
              )}

              <ProgramsSection programs={data.programs} readOnly={noCreate} specializationId={specializationId} entityName={entityName} />

              <ActivityCalendar
                events={calendarEvents}
                onEventPress={(event) => navigation.navigate('TalentActivityDetail', { id: event.id, entityName })}
                onNewActivity={noCreate ? undefined : () => navigation.navigate('AddTalentActivity', { specializationId })}
              />

              <OutputsSection outputs={data.outputs} readOnly={noCreate} projectId={firstProjectId} specializationId={specializationId} />

              {/* Development Plan — hidden for students */}
              {!isStudent && (
                <DevelopmentPlan plan={data.developmentPlan} entityId={specializationId} entityType="specialization" planFileName={planFileName} planAttachments={data.planAttachments} readOnly={isCoordinator} />
              )}

              {/* Coordinators table — hidden for coordinators and students */}
              {!isCoordinator && !isStudent && <DetailTeachersTable specializationId={specializationId} readOnly={readOnly} canManage={canManageCoordinators} entityName={entityName} />}

              {/* Students table — hidden for students */}
              {!isStudent && <StudentsTable readOnly={readOnly} specializationId={specializationId} entityName={entityName} />}
            </View>
          )}
        </View>
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
  },
  sections: {
    gap: 20,
  },
  budgetCard: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    alignItems: 'flex-end',
  },
  budgetValue: {
    fontSize: 22,
    fontFamily: FONTS.bold,
    color: '#A9211F',
  },
  budgetLabel: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#64748B',
    marginTop: 4,
  },
});
